import { Stack } from '@mui/material';
import { FC, Fragment, SVGProps, useState } from 'react';

import { SocialPlatform } from '@/api/profile/queries';
import DiscordIcon from '@/assets/svg/socials/discord.svg';
import FacebookIcon from '@/assets/svg/socials/facebook.svg';
import InstagramIcon from '@/assets/svg/socials/instagram.svg';
import LinkedInIcon from '@/assets/svg/socials/linkedin-white.svg';
import TelegramIcon from '@/assets/svg/socials/telegram.svg';
import WeChatIcon from '@/assets/svg/socials/we-chat.svg';
import WhatsappIcon from '@/assets/svg/socials/whatsapp.svg';
import XIcon from '@/assets/svg/socials/x-white.svg';
import ArrowIcon from '@/assets/svg/ui-elements/arrow-back.svg';
import CheckIcon from '@/assets/svg/ui-elements/check.svg';
import CloseIcon from '@/assets/svg/ui-elements/close.svg';
import PlusIcon from '@/assets/svg/ui-elements/plus.svg';
import { useIsMobile } from '@/global-hooks/use-is-mobile';
import { PLATFORMS } from '@/pages/settings/modules/my-profile-redesign/public-tab/social-platforms';
import {
  Button,
  IconButton,
  Input,
  Modal,
  Separator,
  theme,
  Typography,
} from '@/shared/design-system';

import { SocialProfileBlockWrapper, SocialProfileDialogRow, SocialProfileRow } from './styled';

export type SocialProfileKey = Exclude<SocialPlatform, 'Website'>;

export type SocialProfile = {
  platform: SocialProfileKey;
  value: string;
};

export const PLATFORM_ICONS: Record<SocialProfileKey, FC<SVGProps<SVGSVGElement>>> = {
  LinkedIn: LinkedInIcon,
  X: XIcon,
  Telegram: TelegramIcon,
  WhatsApp: WhatsappIcon,
  WeChat: WeChatIcon,
  Instagram: InstagramIcon,
  Facebook: FacebookIcon,
  Discord: DiscordIcon,
};

const NETWORKS = (Object.keys(PLATFORMS) as SocialPlatform[]).filter((p) => p !== 'Website');

interface SocialProfilesBlockProps {
  value: SocialProfile[];
  onChange: (_profiles: SocialProfile[]) => void;
}

export const SocialProfilesBlock = ({ value, onChange }: SocialProfilesBlockProps) => {
  const isMobile = useIsMobile();

  const [pickerOpen, setPickerOpen] = useState(false);
  const [draftPlatform, setDraftPlatform] = useState<SocialProfileKey | null>(null);
  const [draftValue, setDraftValue] = useState('');
  const [draftError, setDraftError] = useState('');

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const closeModal = () => {
    setPickerOpen(false);
  };

  const handleSelect = (platform: SocialProfileKey) => {
    setDraftPlatform(platform);
    setDraftValue('');
    setDraftError('');
    closeModal();
  };

  const handleOpenPicker = () => {
    if (draftPlatform !== null && !draftValue.trim()) {
      setDraftPlatform(null);
    }
    setPickerOpen(true);
  };

  const handleDraftChange = (v: string) => {
    setDraftValue(v);
    if (draftPlatform) {
      setDraftError(PLATFORMS[draftPlatform].validate(v));
    }
  };

  const handleConfirmDraft = () => {
    if (!draftPlatform || !draftValue.trim()) return;
    const error = PLATFORMS[draftPlatform].validate(draftValue.trim());
    if (error) {
      setDraftError(error);
      return;
    }
    onChange([...value, { platform: draftPlatform, value: draftValue.trim() }]);
    setDraftPlatform(null);
    setDraftValue('');
    setDraftError('');
  };

  const handleDiscardDraft = () => {
    setDraftPlatform(null);
    setDraftValue('');
    setDraftError('');
  };

  const DraftIcon = draftPlatform ? PLATFORM_ICONS[draftPlatform] : null;

  return (
    <>
      <SocialProfileBlockWrapper>
        {value.map((profile, index) => {
          const Icon = PLATFORM_ICONS[profile.platform];
          return (
            <SocialProfileRow key={index}>
              <Stack alignItems="center" gap="10px">
                <Icon width={14} height={14} />
                <Typography variant="body3" color={theme.colors.white[60]}>
                  {PLATFORMS[profile.platform].label}
                </Typography>
              </Stack>

              <Stack alignItems="center" gap="12px">
                <Typography variant="body3">{profile.value}</Typography>
                <IconButton
                  color="tertiary"
                  variant="outline"
                  size="xs"
                  onClick={() => handleRemove(index)}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            </SocialProfileRow>
          );
        })}

        {draftPlatform !== null && DraftIcon !== null && (
          <SocialProfileRow>
            <Stack alignItems="center" gap="10px">
              <DraftIcon width={14} height={14} />
              <Typography variant="body3" color={theme.colors.white[60]}>
                {PLATFORMS[draftPlatform].label}
              </Typography>
            </Stack>

            <Stack alignItems="center" gap="24px">
              <Input
                size="sm"
                value={draftValue}
                placeholder={PLATFORMS[draftPlatform].placeholder}
                onChange={(e) => handleDraftChange(e.target.value)}
                state={draftError ? 'destructive' : 'default'}
              />

              <Stack alignItems="center" gap="8px">
                <IconButton color="primary" variant="solid" size="xs" onClick={handleConfirmDraft}>
                  <CheckIcon />
                </IconButton>
                <IconButton
                  color="tertiary"
                  variant="outline"
                  size="xs"
                  onClick={handleDiscardDraft}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Stack>
          </SocialProfileRow>
        )}

        <SocialProfileRow>
          <Button color="tertiary" variant="ghost" onClick={handleOpenPicker}>
            <PlusIcon width={14} height={14} />
            Add Social Profile
          </Button>
        </SocialProfileRow>
      </SocialProfileBlockWrapper>

      <Modal open={pickerOpen} onOpenChange={(isOpen) => !isOpen && closeModal()}>
        <Stack
          flexDirection="column"
          padding={isMobile ? '16px' : '24px'}
          width={isMobile ? '100%' : 600}
          gap="24px"
        >
          <Stack flexDirection="column" gap="16px">
            <Stack alignItems="center">
              <Stack alignItems="center" gap="8px" flex={1}>
                <IconButton color="tertiary" variant="ghost" onClick={closeModal}>
                  <ArrowIcon />
                </IconButton>
                <Typography variant="h6">Social Profile</Typography>
              </Stack>

              <IconButton color="tertiary" variant="ghost" onClick={closeModal}>
                <CloseIcon />
              </IconButton>
            </Stack>

            <Separator />
          </Stack>

          <Stack flexDirection="column" gap="8px">
            {NETWORKS.map((platform, idx) => (
              <Fragment key={platform}>
                <SocialProfileDialogRow onClick={() => handleSelect(platform)}>
                  <Typography variant="body2">{PLATFORMS[platform].label}</Typography>
                </SocialProfileDialogRow>
                {idx < NETWORKS.length - 1 && <Separator />}
              </Fragment>
            ))}
          </Stack>
        </Stack>
      </Modal>
    </>
  );
};
